import { Snackbar } from '@mui/material';
import { Alert } from '@mui/material';
import { Button as AntdButton, Input, Modal, Table } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAllDistrictTeacher, useCurrentUser } from 'store/users/selectors';
import styled from 'styled-components';
import color from 'utils/constants/color';
import { useShareNotification } from 'utils/methods/firebaseShareAssignment';
import { filter } from 'lodash';
import {Share} from '@mui/icons-material';
import { handleShareSoyStoryToTeacher } from 'store/StoryAiCofig/thunks';
import { useCurrentClass } from 'store/classes/selectors';
import moment from 'moment-timezone';
import useBookAvailabilityCheck from 'components/hooks/books/useBookAvailabilityCheck';
import SentryWrap from 'components/Sentry';

const CustomTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: rgb(239, 246, 254);
  }
  .ant-table-tbody > tr > td {
    background-color: #fff;
  }
  .ant-table-header table,
  .ant-table-body table {
    table-layout: fixed;
    width: 100% !important;
  }
`;

const TableContainer = styled.div`
  margin-bottom: 20px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
  background: #fff;
  border-radius: 20px;
  position: relative;
  border: 1px solid;
  margin-top: 2px;
  margin-bottom: 10px;
  max-width: '250px';
`;

const StyledInput = styled(Input)`
  border: none;
  outline: none;
  background: transparent !important;
  &:focus {
    box-shadow: none;
  }
  flex-grow: 1;
`;

const StoryShare = ({ storyInfo }) => {
    const dispatch = useDispatch();
    const currentUser = useCurrentUser();
    const allDistrictTeacher = useAllDistrictTeacher();
    const { createShareNotification } = useShareNotification();
    const [shareLoader, setShareLoader] = useState(false);
    const [shareModal, setShareModal] = useState(false);
    const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 8 });
    const shared_list = useMemo(()=>storyInfo&&storyInfo.shared_with?storyInfo.shared_with:[],[storyInfo])
    const filterShareBy = useMemo(()=>filter( allDistrictTeacher,(item) => item?.id !== currentUser.id),[allDistrictTeacher,currentUser]);
    const [searchTeacher, setSearchTeacher] = useState(filterShareBy);
    const [alert, setAlert] = useState({
        open: false,
        message: '',
    });

    const currentClass = useCurrentClass();
    const storyChapter = storyInfo?.chapter;
    const checkBookAvailability = useBookAvailabilityCheck(storyChapter, 'Soy Historias');

    useEffect(() => {
        if (shareModal) {
            document.body.style.overflowX = 'hidden';
        } else {
            document.body.style.overflowX = 'auto';
        }
    }, [shareModal]);

    useEffect(() => {
        if (shareModal) {
            document.body.classList.add("body-overflow-hidden");
        }
        return () => {
            document.body.classList.remove("body-overflow-hidden");
        };
    }, [shareModal]);


    const hideShareModal = () => {
        setShareModal(false);
        setTablePagination({ current: 1, pageSize: 8 });
    };

    const ViewShareModal = () => {
        setShareModal(true);
    };

    const handleSend = async (selectedUsers) => {
        if (!checkBookAvailability()) {
            return;
        }
        
        setShareLoader(selectedUsers);
        const data = {
            id:storyInfo.id,
            share_list: [selectedUsers, ...shared_list],
            share_to:selectedUsers,
            share_by:{
                id:currentUser.id,
                firstName: currentUser?.firstName,
                lastName: currentUser?.lastName,
            },
            shared_time: moment().toISOString()
        };
        const { shared_data } = await dispatch(handleShareSoyStoryToTeacher(data));
        if (shared_data) {
            const notificationData = {
                id:shared_data.id,
                shared_data:shared_data,
                notification_list: [selectedUsers],
                createdBy:currentUser.id,
                checkBookExist: false,
                displayName: storyInfo?.story_title || 'Shared Content',
                shared_time: data.shared_time,
                data: {
                    class_id: currentClass.id,
                    chapter: storyInfo?.chapter || '',
                    assignmentType: 'story',
                    storyInstruction: storyInfo?.story_instruction || storyInfo?.storyInstruction || '',
                },
                message: `${currentUser?.firstName} ${currentUser?.lastName} Share '${storyInfo?.story_title||''}' Soy Historias`,
                url: window.location.pathname + window.location.search + window.location.hash,
            };
            createShareNotification(notificationData, storyInfo?.user_id);
        }
        setShareLoader(false);
        setAlert({
            open: true,
            message: 'Soy Historias shared successfully',
        });
    };

    const handleCloseAlert = () => {
        setAlert({
            open: false,
            message: '',
        });
    };

    const handlePaginationChange = (page, size) => {
        setTablePagination((prev) => ({
            current: page,
            pageSize:
                size !== undefined && size !== null ? Number(size) : prev.pageSize,
        }));
    };

    const handleInputChange = (text) => {
        setTablePagination((prev) => ({ ...prev, current: 1 }));
        const normalize = (value) =>
            String(value ?? '')
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '');

        const query = normalize(text);
        if (!query) {
            setSearchTeacher(filterShareBy);
            return;
        }

        const filterBySearch = filterShareBy?.filter((obj) => {
            const firstName = normalize(obj?.firstName);
            const lastName = normalize(obj?.lastName);
            const fullName = `${firstName}${lastName}`;
            const reverseFullName = `${lastName}${firstName}`;

            return (
                firstName.includes(query) ||
                lastName.includes(query) ||
                fullName.includes(query) ||
                reverseFullName.includes(query)
            );
        });

        setSearchTeacher(filterBySearch);
    };

    const columns = [
        {
            title: 'Name',
            key: 'name',
            ellipsis: true,
            width: 420,
            render: (render) => `${render.firstName} ${render.lastName}`,
        },
        {
            title: 'Status',
            key: 'status',
            width: 148,
            align: 'left',
            render: (render) => (
                <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                        if (!shared_list?.includes(render?.id)) {
                            handleSend(render?.id);
                        }
                    }}
                >
                    <span
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <AntdButton
                            type='primary'
                            loading={shareLoader === render?.id}
                            style={{
                                borderRadius: 10,
                                minWidth: 108,
                                backgroundColor: shared_list?.includes(render?.id)
                                    ? color.greentrue
                                    : color.green100,
                                border: '0px',
                                cursor: shared_list?.includes(render?.id) ? 'default' : 'pointer',
                            }}
                        >
                            {shared_list?.includes(render?.id) ? 'Shared' : 'Share'}
                        </AntdButton>
                    </span>
                </div>
            ),
        },
    ];

    return (
        <SentryWrap>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={alert.open}
                autoHideDuration={3000}
                onClose={handleCloseAlert}
            >
                <Alert onClose={handleCloseAlert} severity='success'>
                    <strong>ALERT:</strong> {alert.message}
                </Alert>
            </Snackbar>
            <Modal
                open={shareModal}
                onCancel={hideShareModal}
                className='activeStudentModal'
            >
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                    }}
                >
                    <InputContainer>
                        <StyledInput
                            // ref={inputRef}
                            // value={message}
                            onChange={(e) => handleInputChange(e.target.value)}
                            placeholder='Search District Teacher'
                            maxLength={60}
                            autoFocus
                        />
                    </InputContainer>
                </div>
                <div>
                    <div className='activeStudentMdlContent'>
                        <TableContainer>
                            <CustomTable
                                rowKey='id'
                                columns={columns}
                                dataSource={searchTeacher}
                                scroll={{ x: 568, y: 520 }}
                                sticky
                                pagination={{
                                    position: 'bottomRight',
                                    current: tablePagination.current,
                                    pageSize: tablePagination.pageSize,
                                    pageSizeOptions: ['8', '10', '20', '50', '100'],
                                    onChange: handlePaginationChange,
                                    showSizeChanger: {
                                        getPopupContainer: () => document.body,
                                        showSearch: false,
                                    },
                                }}
                            />
                        </TableContainer>
                    </div>
                </div>
            </Modal>
            <AntdButton
                className="share-button-super-lesson"
                type="primary"
                style={{
                    borderRadius: 10,
                    border: "0px",
                    color: "black",
                    backgroundColor: "#03989e"
                }}
                onClick={() => {
                    ViewShareModal();
                }}
            >
                <Share style={{color:'white'}} />
            </AntdButton>
        </SentryWrap>
    );
};

export default StoryShare;
